import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { MagnifyingGlassIcon, FunnelIcon, ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface Recipe {
  _id: string;
  title: string;
  titleArabic?: string;
  description?: string;
  descriptionArabic?: string;
  cuisine: string;
  cuisineArabic?: string;
  sourceUrl?: string;
  categories?: string[];
  subcategories?: string[];
  ingredients: any[];
  steps: any[];
  timeMin: number;
  difficulty: string;
  equipment?: string[];
  tags?: string[];
  tagsArabic?: string[];
  dietaryTags?: string[];
  dietaryTagsArabic?: string[];
  proTips?: string[];
  proTipsArabic?: string[];
  isTrending?: boolean;
  seasonalTags?: string[];
  cookingMethods?: any[];
  media?: {
    images?: string[];
    videos?: string[];
    arVideos?: string[];
    thumbnail?: string;
  };
  nutrition?: {
    calories?: number;
    protein?: number;
    carbs?: number;
    fats?: number;
    fiber?: number;
    sugar?: number;
  };
  costEstimateEGP: number;
  servings: number;
  isPublished: boolean;
  buyBoxAllowed?: boolean;
  buyPreparedMealAllowed?: boolean;
  createdBy?: {
    name: string;
    email: string;
  };
}

export default function RecipesTable() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const isRtl = i18n.language === 'ar';
  const [categories, setCategories] = useState<any[]>([]);
  const [subcategories, setSubcategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'published' | 'unpublished'>('all');
  const [showFilters, setShowFilters] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(20);
  
  // Search and filter states
  const [search, setSearch] = useState('');
  const [activeSearch, setActiveSearch] = useState(''); // The search term actually used for API calls
  const [cuisineFilter, setCuisineFilter] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('');
  const [timeMinFilter, setTimeMinFilter] = useState('');
  const [timeMaxFilter, setTimeMaxFilter] = useState('');
  const [costMinFilter, setCostMinFilter] = useState('');
  const [costMaxFilter, setCostMaxFilter] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedSubcategories, setSelectedSubcategories] = useState<string[]>([]);
  const [selectedEquipment, setSelectedEquipment] = useState<string[]>([]);
  
  // Active filters (applied to API calls)
  const [activeCuisineFilter, setActiveCuisineFilter] = useState('');
  const [activeDifficultyFilter, setActiveDifficultyFilter] = useState('');
  const [activeTimeMinFilter, setActiveTimeMinFilter] = useState('');
  const [activeTimeMaxFilter, setActiveTimeMaxFilter] = useState('');
  const [activeCostMinFilter, setActiveCostMinFilter] = useState('');
  const [activeCostMaxFilter, setActiveCostMaxFilter] = useState('');
  const [activeCategories, setActiveCategories] = useState<string[]>([]);
  const [activeSubcategories, setActiveSubcategories] = useState<string[]>([]);
  const [activeEquipment, setActiveEquipment] = useState<string[]>([]);
  const [equipment, setEquipment] = useState<any[]>([]);
  
  // Dropdown states
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showSubcategoryDropdown, setShowSubcategoryDropdown] = useState(false);
  const [showEquipmentDropdown, setShowEquipmentDropdown] = useState(false);
  const [categorySearch, setCategorySearch] = useState('');
  const [subcategorySearch, setSubcategorySearch] = useState('');
  const [equipmentSearch, setEquipmentSearch] = useState('');
  
  // Refs for click outside
  const categoryDropdownRef = useRef<HTMLDivElement>(null);
  const subcategoryDropdownRef = useRef<HTMLDivElement>(null);
  const equipmentDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadCategories();
    loadEquipment();
  }, []);

  // Handle click outside for dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target as Node)) {
        setShowCategoryDropdown(false);
      }
      if (subcategoryDropdownRef.current && !subcategoryDropdownRef.current.contains(event.target as Node)) {
        setShowSubcategoryDropdown(false);
      }
      if (equipmentDropdownRef.current && !equipmentDropdownRef.current.contains(event.target as Node)) {
        setShowEquipmentDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setCurrentPage(1); // Reset to first page when filters change
  }, [filter, activeSearch, activeCuisineFilter, activeDifficultyFilter, activeTimeMinFilter, activeTimeMaxFilter, activeCostMinFilter, activeCostMaxFilter, activeCategories, activeSubcategories, activeEquipment]);

  useEffect(() => {
    loadRecipes();
  }, [currentPage, limit, filter, activeSearch, activeCuisineFilter, activeDifficultyFilter, activeTimeMinFilter, activeTimeMaxFilter, activeCostMinFilter, activeCostMaxFilter, activeCategories, activeSubcategories, activeEquipment]);

  const loadRecipes = async () => {
    try {
      setLoading(true);
      const params: any = { 
        page: currentPage,
        limit: limit,
      };
      
      // Status filter
      if (filter === 'published') params.isPublished = 'true';
      if (filter === 'unpublished') params.isPublished = 'false';
      
      // Search
      if (activeSearch) params.search = activeSearch;
      
      // Filters (use active filters, not the temporary ones)
      if (activeCuisineFilter) params.cuisine = activeCuisineFilter;
      if (activeDifficultyFilter) params.difficulty = activeDifficultyFilter;
      if (activeTimeMinFilter) params.timeMin = activeTimeMinFilter;
      if (activeTimeMaxFilter) params.timeMax = activeTimeMaxFilter;
      if (activeCostMinFilter) params.costMin = activeCostMinFilter;
      if (activeCostMaxFilter) params.costMax = activeCostMaxFilter;
      if (activeCategories.length > 0) params.categories = activeCategories;
      if (activeSubcategories.length > 0) params.subcategories = activeSubcategories;
      if (activeEquipment.length > 0) params.equipmentIds = activeEquipment;
      
      const response = await api.get('/admin/recipes', { params });
      const recipesData = response.data.data || [];
      const recipesNormalized = recipesData.map((recipe: any) => ({
        ...recipe,
        buyBoxAllowed: recipe.buyBoxAllowed ?? false,
        buyPreparedMealAllowed: recipe.buyPreparedMealAllowed ?? false,
      }));
      setRecipes(recipesNormalized);
      setTotalPages(response.data.pages || 1);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error loading recipes:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await api.get('/admin/categories?limit=1000');
      const allCats = response.data.data || response.data || [];
      setCategories(allCats.filter((c: any) => c.type === 'main'));
      setSubcategories(allCats.filter((c: any) => c.type === 'sub'));
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadEquipment = async () => {
    try {
      const response = await api.get('/admin/equipment?limit=1000&activeOnly=true');
      const equipmentData = response.data.data || response.data || [];
      setEquipment(equipmentData);
    } catch (error) {
      console.error('Error loading equipment:', error);
    }
  };

  const handleSearch = () => {
    setActiveSearch(search);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const applyFilters = () => {
    // Apply all temporary filters to active filters
    setActiveCuisineFilter(cuisineFilter);
    setActiveDifficultyFilter(difficultyFilter);
    setActiveTimeMinFilter(timeMinFilter);
    setActiveTimeMaxFilter(timeMaxFilter);
    setActiveCostMinFilter(costMinFilter);
    setActiveCostMaxFilter(costMaxFilter);
    setActiveCategories(selectedCategories);
    setActiveSubcategories(selectedSubcategories);
    setActiveEquipment(selectedEquipment);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearch('');
    setActiveSearch('');
    setCuisineFilter('');
    setDifficultyFilter('');
    setTimeMinFilter('');
    setTimeMaxFilter('');
    setCostMinFilter('');
    setCostMaxFilter('');
    setSelectedCategories([]);
    setSelectedSubcategories([]);
    setSelectedEquipment([]);
    setCategorySearch('');
    setSubcategorySearch('');
    setEquipmentSearch('');
    // Clear active filters too
    setActiveCuisineFilter('');
    setActiveDifficultyFilter('');
    setActiveTimeMinFilter('');
    setActiveTimeMaxFilter('');
    setActiveCostMinFilter('');
    setActiveCostMaxFilter('');
    setActiveCategories([]);
    setActiveSubcategories([]);
    setActiveEquipment([]);
    setCurrentPage(1);
  };

  const togglePublish = async (id: string, currentStatus: boolean) => {
    try {
      await api.put(`/admin/recipes/${id}/publish`, {
        isPublished: !currentStatus,
      });
      loadRecipes();
    } catch (error) {
      console.error('Error toggling publish status:', error);
      alert(t('recipes.failedToUpdateStatus'));
    }
  };

  const toggleBuyBoxAllowed = async (id: string, current: boolean) => {
    try {
      await api.put(`/admin/recipes/${id}`, { buyBoxAllowed: !current });
      loadRecipes();
    } catch (error) {
      console.error('Error toggling box allowed:', error);
      alert(t('recipes.failedToUpdateBuying'));
    }
  };

  const toggleBuyPreparedMealAllowed = async (id: string, current: boolean) => {
    try {
      await api.put(`/admin/recipes/${id}`, { buyPreparedMealAllowed: !current });
      loadRecipes();
    } catch (error) {
      console.error('Error toggling prepared meal allowed:', error);
      alert(t('recipes.failedToUpdateBuying'));
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm(t('recipes.confirmDelete'))) return;
    try {
      await api.delete(`/admin/recipes/${id}`);
      loadRecipes();
    } catch (error: any) {
      console.error('Error deleting recipe:', error);
      alert(error.response?.data?.message || t('recipes.failedToDelete'));
    }
  };


  if (loading) {
    return <div className="text-center py-8">{t('recipes.loading')}</div>;
  }

  return (
    <div className={`space-y-4 w-full ${isRtl ? 'text-right' : ''}`} dir={isRtl ? 'rtl' : 'ltr'}>
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className={`flex flex-col md:flex-row gap-4 ${isRtl ? 'md:flex-row-reverse' : ''}`}>
          <div className="flex-1 relative flex items-center">
            <MagnifyingGlassIcon className={`absolute w-5 h-5 text-gray-400 ${isRtl ? 'right-3 left-auto' : 'left-3'}`} />
            <input
              type="text"
              placeholder={t('recipes.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyPress={handleKeyPress}
              className={`w-full py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent ${isRtl ? 'pr-10 pl-20' : 'pl-10 pr-20'}`}
            />
            <button
              onClick={handleSearch}
              className={`absolute px-4 py-1.5 bg-primary-orange hover:bg-orange-600 text-white rounded-lg text-sm transition-colors ${isRtl ? 'left-2 right-auto' : 'right-2'}`}
            >
              {t('common.search')}
            </button>
          </div>
          <div className={`flex gap-2 ${isRtl ? 'flex-row-reverse' : ''}`}>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as any)}
              className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-primary-orange focus:border-transparent"
            >
              <option value="all">{t('recipes.allRecipes')}</option>
              <option value="published">{t('recipes.published')}</option>
              <option value="unpublished">{t('recipes.unpublished')}</option>
            </select>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center px-4 py-2 border rounded-lg transition-colors ${isRtl ? 'flex-row-reverse gap-2' : 'space-x-2'} ${showFilters ? 'bg-primary-orange text-white border-primary-orange' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
            >
              <FunnelIcon className="w-5 h-5" />
              <span>{t('recipes.filters')}</span>
            </button>
            <button
              onClick={() => navigate('/recipes/new')}
              className="bg-primary-orange hover:bg-orange-600 text-white px-4 py-2 rounded-lg transition-colors"
            >
              + {t('recipes.addRecipe')}
            </button>
          </div>
        </div>
        
        {/* Advanced Filters */}
        {showFilters && (
          <>
          <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="relative" ref={categoryDropdownRef}>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('recipes.categories')}</label>
              
              {/* Selected Categories Display */}
              {selectedCategories.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {selectedCategories.map((catId) => {
                    const cat = categories.find(c => c._id === catId);
                    if (!cat) return null;
                    return (
                      <span
                        key={catId}
                        className="inline-flex items-center gap-1 text-xs bg-primary-orange/10 text-primary-orange px-2 py-1 rounded"
                      >
                        {cat.name || cat.nameEn || ''}
                        <button
                          type="button"
                          onClick={() => setSelectedCategories(selectedCategories.filter(id => id !== catId))}
                          className="hover:text-primary-orange/70"
                        >
                          <XMarkIcon className="w-3 h-3" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
              
              {/* Dropdown Button */}
              <button
                type="button"
                onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                className="w-full flex items-center justify-between border border-gray-300 rounded-lg px-3 py-2 bg-white hover:bg-gray-50 text-sm"
              >
                <span className="text-gray-500">{t('recipes.selectCategories')}</span>
                <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${showCategoryDropdown ? 'transform rotate-180' : ''}`} />
              </button>
              
              {/* Dropdown Menu */}
              {showCategoryDropdown && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden">
                  <div className="p-2 border-b border-gray-200">
                    <div className="relative">
                      <MagnifyingGlassIcon className="absolute left-2 top-2.5 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder={t('recipes.searchCategories')}
                        value={categorySearch}
                        onChange={(e) => setCategorySearch(e.target.value)}
                        className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  </div>
                  <div className="overflow-y-auto max-h-48">
                    {categories
                      .filter((cat) => {
                        const searchLower = categorySearch.toLowerCase();
                        const nameEn = (cat.name || cat.nameEn || '').toLowerCase();
                        const nameAr = (cat.nameArabic || cat.nameAr || '').toLowerCase();
                        return nameEn.includes(searchLower) || nameAr.includes(searchLower);
                      })
                      .map((cat) => {
                        const isSelected = selectedCategories.includes(cat._id);
                        return (
                          <label
                            key={cat._id}
                            className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedCategories([...selectedCategories, cat._id]);
                                } else {
                                  setSelectedCategories(selectedCategories.filter(id => id !== cat._id));
                                }
                              }}
                              className="mr-2 rounded border-gray-300 text-primary-orange focus:ring-primary-orange"
                              onClick={(e) => e.stopPropagation()}
                            />
                            <span className="text-sm text-gray-700">
                              {cat.name || cat.nameEn || ''} {cat.nameArabic || cat.nameAr ? `(${cat.nameArabic || cat.nameAr})` : ''}
                            </span>
                          </label>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>
            <div className="relative" ref={subcategoryDropdownRef}>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('recipes.subcategories')}</label>
              
              {/* Selected Subcategories Display */}
              {selectedSubcategories.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {selectedSubcategories.map((catId) => {
                    const cat = subcategories.find(c => c._id === catId);
                    if (!cat) return null;
                    return (
                      <span
                        key={catId}
                        className="inline-flex items-center gap-1 text-xs bg-primary-orange/10 text-primary-orange px-2 py-1 rounded"
                      >
                        {cat.name || cat.nameEn || ''}
                        <button
                          type="button"
                          onClick={() => setSelectedSubcategories(selectedSubcategories.filter(id => id !== catId))}
                          className="hover:text-primary-orange/70"
                        >
                          <XMarkIcon className="w-3 h-3" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
              
              {/* Dropdown Button */}
              <button
                type="button"
                onClick={() => setShowSubcategoryDropdown(!showSubcategoryDropdown)}
                className="w-full flex items-center justify-between border border-gray-300 rounded-lg px-3 py-2 bg-white hover:bg-gray-50 text-sm"
              >
                <span className="text-gray-500">Select subcategories...</span>
                <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${showSubcategoryDropdown ? 'transform rotate-180' : ''}`} />
              </button>
              
              {/* Dropdown Menu */}
              {showSubcategoryDropdown && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden">
                  <div className="p-2 border-b border-gray-200">
                    <div className="relative">
                      <MagnifyingGlassIcon className="absolute left-2 top-2.5 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder={t('recipes.searchSubcategories')}
                        value={subcategorySearch}
                        onChange={(e) => setSubcategorySearch(e.target.value)}
                        className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  </div>
                  <div className="overflow-y-auto max-h-48">
                    {subcategories
                      .filter((cat) => {
                        const searchLower = subcategorySearch.toLowerCase();
                        const nameEn = (cat.name || cat.nameEn || '').toLowerCase();
                        const nameAr = (cat.nameArabic || cat.nameAr || '').toLowerCase();
                        return nameEn.includes(searchLower) || nameAr.includes(searchLower);
                      })
                      .map((cat) => {
                        const isSelected = selectedSubcategories.includes(cat._id);
                        return (
                          <label
                            key={cat._id}
                            className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedSubcategories([...selectedSubcategories, cat._id]);
                                } else {
                                  setSelectedSubcategories(selectedSubcategories.filter(id => id !== cat._id));
                                }
                              }}
                              className="mr-2 rounded border-gray-300 text-primary-orange focus:ring-primary-orange"
                              onClick={(e) => e.stopPropagation()}
                            />
                            <span className="text-sm text-gray-700">
                              {cat.name || cat.nameEn || ''} {cat.nameArabic || cat.nameAr ? `(${cat.nameArabic || cat.nameAr})` : ''}
                            </span>
                          </label>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>
            <div className="relative" ref={equipmentDropdownRef}>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('recipes.equipment')}</label>
              
              {/* Selected Equipment Display */}
              {selectedEquipment.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {selectedEquipment.map((eqId) => {
                    const eq = equipment.find(e => e._id === eqId);
                    if (!eq) return null;
                    return (
                      <span
                        key={eqId}
                        className="inline-flex items-center gap-1 text-xs bg-primary-orange/10 text-primary-orange px-2 py-1 rounded"
                      >
                        {eq.name?.en || eq.name || ''}
                        <button
                          type="button"
                          onClick={() => setSelectedEquipment(selectedEquipment.filter(id => id !== eqId))}
                          className="hover:text-primary-orange/70"
                        >
                          <XMarkIcon className="w-3 h-3" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
              
              {/* Dropdown Button */}
              <button
                type="button"
                onClick={() => setShowEquipmentDropdown(!showEquipmentDropdown)}
                className="w-full flex items-center justify-between border border-gray-300 rounded-lg px-3 py-2 bg-white hover:bg-gray-50 text-sm"
              >
                <span className="text-gray-500">{t('recipes.selectEquipment')}</span>
                <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${showEquipmentDropdown ? 'transform rotate-180' : ''}`} />
              </button>
              
              {/* Dropdown Menu */}
              {showEquipmentDropdown && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden">
                  <div className="p-2 border-b border-gray-200">
                    <div className="relative">
                      <MagnifyingGlassIcon className="absolute left-2 top-2.5 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder={t('recipes.searchEquipment')}
                        value={equipmentSearch}
                        onChange={(e) => setEquipmentSearch(e.target.value)}
                        className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  </div>
                  <div className="overflow-y-auto max-h-48">
                    {equipment
                      .filter((eq) => {
                        const searchLower = equipmentSearch.toLowerCase();
                        const nameEn = (eq.name?.en || eq.name || '').toLowerCase();
                        const nameAr = (eq.name?.ar || '').toLowerCase();
                        return nameEn.includes(searchLower) || nameAr.includes(searchLower);
                      })
                      .map((eq) => {
                        const isSelected = selectedEquipment.includes(eq._id);
                        return (
                          <label
                            key={eq._id}
                            className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedEquipment([...selectedEquipment, eq._id]);
                                } else {
                                  setSelectedEquipment(selectedEquipment.filter(id => id !== eq._id));
                                }
                              }}
                              className="mr-2 rounded border-gray-300 text-primary-orange focus:ring-primary-orange"
                              onClick={(e) => e.stopPropagation()}
                            />
                            <span className="text-sm text-gray-700">
                              {eq.name?.en || eq.name || ''} {eq.name?.ar ? `(${eq.name.ar})` : ''}
                            </span>
                          </label>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('recipes.cuisine')}</label>
              <input
                type="text"
                placeholder={t('recipes.anyCuisine')}
                value={cuisineFilter}
                onChange={(e) => setCuisineFilter(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('recipes.difficulty')}</label>
              <select
                value={difficultyFilter}
                onChange={(e) => setDifficultyFilter(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="">{t('recipes.all')}</option>
                <option value="Beginner">{t('recipes.difficultyBeginner')}</option>
                <option value="Intermediate">{t('recipes.difficultyIntermediate')}</option>
                <option value="Pro">{t('recipes.difficultyPro')}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('recipes.minTimeMin')}</label>
              <input
                type="number"
                placeholder={t('recipes.any')}
                value={timeMinFilter}
                onChange={(e) => setTimeMinFilter(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('recipes.maxTimeMin')}</label>
              <input
                type="number"
                placeholder={t('recipes.any')}
                value={timeMaxFilter}
                onChange={(e) => setTimeMaxFilter(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('recipes.minCostEgp')}</label>
              <input
                type="number"
                placeholder={t('recipes.any')}
                value={costMinFilter}
                onChange={(e) => setCostMinFilter(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('recipes.maxCostEgp')}</label>
              <input
                type="number"
                placeholder={t('recipes.any')}
                value={costMaxFilter}
                onChange={(e) => setCostMaxFilter(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button
              onClick={applyFilters}
              className="px-4 py-2 text-sm bg-primary-orange hover:bg-orange-600 text-white rounded-lg transition-colors font-medium"
            >
              {t('recipes.applyFilters')}
            </button>
            <button
              onClick={clearFilters}
              className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {t('recipes.clearFilters')}
            </button>
          </div>
          </>
        )}
      </div>

      {/* Recipes Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden relative w-full">
        <div className="overflow-x-auto w-full">
          <table className="w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRtl ? 'text-right' : 'text-left'}`}>{t('recipes.titleCol')}</th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRtl ? 'text-right' : 'text-left'}`}>{t('recipes.cuisine')}</th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRtl ? 'text-right' : 'text-left'}`}>{t('recipes.time')}</th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRtl ? 'text-right' : 'text-left'}`}>{t('recipes.difficulty')}</th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRtl ? 'text-right' : 'text-left'}`}>{t('recipes.status')}</th>
                <th className={`px-2 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap ${isRtl ? 'text-right' : 'text-left'}`}>{t('recipes.mealBox')}</th>
                <th className={`px-2 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap ${isRtl ? 'text-right' : 'text-left'}`}>{t('recipes.preparedMeal')}</th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRtl ? 'text-right' : 'text-left'}`}>{t('recipes.actions')}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {recipes.map((recipe) => (
                <tr key={recipe._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{recipe.title}</div>
                    {recipe.titleArabic && (
                      <div className="text-sm text-gray-500">{recipe.titleArabic}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{recipe.cuisine}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{recipe.timeMin} min</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{recipe.difficulty}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        recipe.isPublished
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}
                    >
                      {recipe.isPublished ? t('recipes.published') : t('recipes.unpublished')}
                    </span>
                  </td>
                  <td className="px-2 py-4 whitespace-nowrap">
                    <button
                      onClick={() => toggleBuyBoxAllowed(recipe._id, recipe.buyBoxAllowed ?? false)}
                      className={`px-2 py-1 text-xs font-semibold rounded-full transition-colors cursor-pointer ${
                        recipe.buyBoxAllowed ? 'bg-blue-100 text-blue-800 hover:bg-blue-200' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                      }`}
                      title={recipe.buyBoxAllowed ? t('recipes.disableBuying') : t('recipes.enableBuying')}
                    >
                      {recipe.buyBoxAllowed ? t('recipes.yes') : t('recipes.no')}
                    </button>
                  </td>
                  <td className="px-2 py-4 whitespace-nowrap">
                    <button
                      onClick={() => toggleBuyPreparedMealAllowed(recipe._id, recipe.buyPreparedMealAllowed ?? false)}
                      className={`px-2 py-1 text-xs font-semibold rounded-full transition-colors cursor-pointer ${
                        recipe.buyPreparedMealAllowed ? 'bg-green-100 text-green-800 hover:bg-green-200' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                      }`}
                      title={recipe.buyPreparedMealAllowed ? t('recipes.disableBuying') : t('recipes.enableBuying')}
                    >
                      {recipe.buyPreparedMealAllowed ? t('recipes.yes') : t('recipes.no')}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className={`flex flex-wrap items-center ${isRtl ? 'flex-row-reverse justify-start' : 'justify-end'} gap-3`}>
                      <button
                        onClick={() => navigate(`/recipes/${recipe._id}/edit`)}
                        className="text-primary-orange hover:text-orange-600"
                      >
                        {t('recipes.edit')}
                      </button>
                      <button
                        onClick={() => togglePublish(recipe._id, recipe.isPublished)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        {recipe.isPublished ? t('recipes.unpublish') : t('recipes.publish')}
                      </button>
                      <button
                        onClick={() => handleDelete(recipe._id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        {t('common.delete')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {recipes.length === 0 && (
          <div className="text-center py-8 text-gray-500">{t('recipes.noRecipes')}</div>
        )}
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className={`px-6 py-4 border-t border-gray-200 flex items-center justify-between ${isRtl ? 'flex-row-reverse' : ''}`}>
            <div className={`flex items-center gap-2 ${isRtl ? 'flex-row-reverse' : ''}`}>
              <span className="text-sm text-gray-700">
                {t('common.showing', { from: ((currentPage - 1) * limit) + 1, to: Math.min(currentPage * limit, total), total })}
              </span>
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(parseInt(e.target.value));
                  setCurrentPage(1);
                }}
                className="border border-gray-300 rounded-lg px-2 py-1 text-sm"
              >
                <option value={10}>10 {t('common.perPage')}</option>
                <option value={20}>20 {t('common.perPage')}</option>
                <option value={50}>50 {t('common.perPage')}</option>
                <option value={100}>100 {t('common.perPage')}</option>
              </select>
            </div>
            <div className={`flex items-center gap-2 ${isRtl ? 'flex-row-reverse' : ''}`}>
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                {t('common.previous')}
              </button>
              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-1 rounded-lg text-sm ${
                        currentPage === pageNum
                          ? 'bg-primary-orange text-white'
                          : 'border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                {t('common.next')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
